import { firestore } from '../firebase/config';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { encryptPassword, isPasswordHashed, encryptForAdmin } from './passwordUtils';

/**
 * Migration script to encrypt existing plain text passwords
 * This should be run once to secure existing user accounts
 */
export const migratePasswords = async () => {
  try {
    // Migrate users collection
    const usersRef = collection(firestore, 'users');
    const usersSnapshot = await getDocs(usersRef);
    let usersMigrated = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const password = userData.password;
      
             // Only encrypt if password is not already hashed
       if (password && !isPasswordHashed(password)) {
         try {
           const encryptedPassword = await encryptPassword(password);
           const adminPassword = encryptForAdmin(password);
           await updateDoc(doc(firestore, 'users', userDoc.id), {
             password: encryptedPassword,
             adminPassword: adminPassword
           });
           usersMigrated++;
         } catch (error) {
           console.error(`Failed to migrate user ${userDoc.id}:`, error);
         }
       }
    }
    
    // Migrate students collection
    const studentsRef = collection(firestore, 'students');
    const studentsSnapshot = await getDocs(studentsRef);
    let studentsMigrated = 0;
    
    for (const studentDoc of studentsSnapshot.docs) {
      const studentData = studentDoc.data();
      const password = studentData.password;
      
             // Only encrypt if password is not already hashed
       if (password && !isPasswordHashed(password)) {
         try {
           const encryptedPassword = await encryptPassword(password);
           const adminPassword = encryptForAdmin(password);
           await updateDoc(doc(firestore, 'students', studentDoc.id), {
             password: encryptedPassword,
             adminPassword: adminPassword
           });
           studentsMigrated++;
         } catch (error) {
           console.error(`Failed to migrate student ${studentDoc.id}:`, error);
         }
       }
    }
    
    // Migrate employees collection
    const employeesRef = collection(firestore, 'employees');
    const employeesSnapshot = await getDocs(employeesRef);
    let employeesMigrated = 0;
    
    for (const employeeDoc of employeesSnapshot.docs) {
      const employeeData = employeeDoc.data();
      const password = employeeData.password;
      
             // Only encrypt if password is not already hashed
       if (password && !isPasswordHashed(password)) {
         try {
           const encryptedPassword = await encryptPassword(password);
           const adminPassword = encryptForAdmin(password);
           await updateDoc(doc(firestore, 'employees', employeeDoc.id), {
             password: encryptedPassword,
             adminPassword: adminPassword
           });
           employeesMigrated++;
         } catch (error) {
           console.error(`Failed to migrate employee ${employeeDoc.id}:`, error);
         }
       }
    }
    
    console.log(`Password migration completed!`);
    console.log(`Users migrated: ${usersMigrated}`);
    console.log(`Students migrated: ${studentsMigrated}`);
    console.log(`Employees migrated: ${employeesMigrated}`);
    
    return {
      success: true,
      usersMigrated,
      studentsMigrated,
      employeesMigrated
    };
    
  } catch (error) {
    console.error('Password migration failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if passwords need migration
 */
export const checkPasswordMigration = async () => {
  try {
    const usersRef = collection(firestore, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    let needsMigration = false;
    let totalUsers = 0;
    let unencryptedUsers = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      totalUsers++;
      
      if (userData.password && !isPasswordHashed(userData.password)) {
        unencryptedUsers++;
        needsMigration = true;
      }
    }
    
    return {
      needsMigration,
      totalUsers,
      unencryptedUsers
    };
    
  } catch (error) {
    console.error('Error checking password migration:', error);
    return {
      needsMigration: false,
      error: error.message
    };
  }
}; 